import { ReactNode } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Close, Save, Lock } from '@mui/icons-material';
import ReactGA from 'react-ga4';
import { AUTURA_GRAYSCALE } from 'context/themeContext';
export interface DetailsHeaderProps {
  title: string | ReactNode;
  onCancel?: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
  disableSubmit?: boolean;
  disableCancel?: boolean;
  cancelButtonLabel?: string;
  submitButtonIcon?: ReactNode;
  cancelButtonIcon?: ReactNode;
  readonly?: boolean;
  hideSubmit?: boolean;
  hideCancel?: boolean;
  helperText?: string;
  actions?: ReactNode;
}

const DetailsHeader = ({
  title,
  onCancel,
  onSubmit,
  submitLabel,
  disableSubmit,
  disableCancel,
  cancelButtonLabel,
  submitButtonIcon,
  cancelButtonIcon,
  readonly = false,
  hideSubmit = false,
  hideCancel = false,
  helperText,
  actions,
}: DetailsHeaderProps): ReactNode => {
  const { t } = useTranslation();

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit();
    }
    ReactGA.event({
      category: 'Form',
      action: 'Submit',
      label: submitLabel,
    });
  };

  return (
    <Stack
      direction={'row'}
      display={'flex'}
      alignItems={'center'}
      justifyContent={'space-between'}
      sx={{
        height: 72,
        flexShrink: 0,
        pb: 2,
        borderBottom: { xs: `1px solid ${AUTURA_GRAYSCALE[300]}`, md: 0 },
      }}
    >
      <Stack direction='column'>
        <Typography
          variant='h2'
          textTransform={'capitalize'}
          sx={{ display: { xs: 'none', sm: 'block' } }}
        >
          {title}
        </Typography>
        {helperText && (
          <Typography variant='caption' color='text.secondary'>
            {helperText}
          </Typography>
        )}
      </Stack>
      <Stack
        display={'flex'}
        direction={'row'}
        gap={2}
        sx={{ flexGrow: 1 }}
        justifyContent={'flex-end'}
      >
        {actions}
        {onCancel && !hideCancel && (
          <Button
            onClick={onCancel}
            disabled={disableCancel}
            variant='outlined'
            color='secondary'
            startIcon={cancelButtonIcon || <Close />}
            sx={{
              borderRadius: '8px',
              px: 2,
              py: 1.25,
            }}
          >
            {cancelButtonLabel || t('buttons.cancel')}
          </Button>
        )}
        {!hideSubmit && (
          <Button
            onClick={handleSubmit}
            disabled={disableSubmit || readonly}
            variant='contained'
            color='secondary'
            type='submit'
            sx={{
              borderRadius: '8px',
              px: 2,
              py: 1.25,
              flexGrow: { xs: 1, sm: 0 },
            }}
            endIcon={readonly ? <Lock /> : submitButtonIcon || <Save />}
          >
            {submitLabel || t('buttons.submit')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default DetailsHeader;
